import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Loader2, Mail, Shield, Lock, ArrowRight, CheckCircle, HelpCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import '@/styles/marketing.css';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/v1/auth/forgot-password`, {
        email: email.toLowerCase()
      });

      if (response.data.success) {
        setIsSubmitted(true);
        toast.success('Password reset instructions sent!');
      }
    } catch (error: any) {
      // Even on error, show success message (security: don't reveal if email exists)
      if (error.response?.status === 429) {
        toast.error('Too many requests. Please try again in an hour.');
      } else {
        // Always show success to prevent email enumeration
        setIsSubmitted(true);
        toast.success('Password reset instructions sent!');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="forgot-password-page">
        <div className="forgot-password-container">
          <div className="forgot-password-content">
            {/* Success Icon */}
            <div className="forgot-password-success-icon">
              <Mail size={48} />
            </div>

            {/* Success Message */}
            <div className="forgot-password-header">
              <h2 className="forgot-password-title">Check Your Email</h2>
              <p className="forgot-password-description">
                If an account exists for <span className="forgot-password-email-highlight">{email}</span>,
                you will receive password reset instructions shortly.
              </p>
            </div>

            {/* Instructions */}
            <div className="forgot-password-instructions">
              <h3 className="forgot-password-instructions-title">Next Steps:</h3>
              <ul className="forgot-password-instructions-list">
                <li>
                  <CheckCircle size={18} />
                  <span>Check your email inbox (and spam folder)</span>
                </li>
                <li>
                  <CheckCircle size={18} />
                  <span>Click the reset link within 1 hour</span>
                </li>
                <li>
                  <CheckCircle size={18} />
                  <span>Create a new secure password</span>
                </li>
              </ul>
            </div>

            {/* Actions */}
            <div className="forgot-password-actions">
              <Link to="/login" className="forgot-password-button-primary">
                <ArrowLeftIcon className="forgot-password-button-icon" />
                Back to Login
              </Link>

              <button
                onClick={() => {
                  setIsSubmitted(false);
                  setEmail('');
                }}
                className="forgot-password-button-secondary"
              >
                Try a different email
              </button>
            </div>

            {/* Help */}
            <div className="forgot-password-help">
              <HelpCircle size={18} />
              <p className="forgot-password-help-text">
                Didn't receive an email?{' '}
                <Link to="/help" className="forgot-password-help-link">
                  Contact Support
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="forgot-password-page">
      <div className="forgot-password-container">
        <div className="forgot-password-content">
          {/* Logo Icon */}
          <div className="forgot-password-logo">
            <div className="forgot-password-logo-icon">
              <Lock size={40} />
            </div>
          </div>

          {/* Header */}
          <div className="forgot-password-header">
            <h2 className="forgot-password-title">Reset Your Password</h2>
            <p className="forgot-password-description">
              Enter your email address and we'll send you instructions to reset your password.
            </p>
          </div>

          {/* Form */}
          <form className="forgot-password-form" onSubmit={handleSubmit}>
            <div className="forgot-password-form-group">
              <label htmlFor="email" className="forgot-password-form-label">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="forgot-password-form-input"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="forgot-password-submit-button"
            >
              {isLoading ? (
                <>
                  <Loader2 className="forgot-password-spinner" size={20} />
                  Sending...
                </>
              ) : (
                <>
                  Send Reset Instructions
                  <ArrowRight size={20} />
                </>
              )}
            </button>

            <div className="forgot-password-back-link">
              <Link to="/login" className="forgot-password-link">
                <ArrowLeftIcon className="forgot-password-link-icon" />
                Back to Login
              </Link>
            </div>
          </form>

          {/* Security Notice */}
          <div className="forgot-password-security-notice">
            <div className="forgot-password-security-icons">
              <Shield size={20} />
              <Lock size={20} />
            </div>
            <p className="forgot-password-security-text">
              HIPAA-compliant platform with end-to-end encryption and secure data handling
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
